// The bloods slip: lab results printed on paper on the light page. Values count up as the slip prints, flags pop
// with ↑H or ↓L (never colour alone), and for "tap the culprit" every row is a 48 px button. While the question is open
// the rows read as buttons (an ink edge with a lip, a chevron, a "Tap a row" cue in the head, pressed down on tap, the
// option buttons' squash on lock-in); once locked they keep their shape and lose the cue. At the reveal the slip prints
// flat again: the culprit is circled in green pen and a wrong pick is struck through in red. A blood gas prints as a
// blood gas analyser's report instead (gas.ts): the sample type and oxygen in the header, the analyser's names and order,
// a bracketed reference range and an arrow beside each value out of range; a tap still answers the card's own row.
// The pen measures in layout pixels, so its marks land on the result on the presenter view's scaled stage as on a phone.
import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { canAnimate, prefersReducedMotion } from '@/motion/reduced';
import type { BloodsGraphic, BloodsRow } from '@shared/types';
import { useCountUps } from './countUp';
import { chem } from './chem';
import { gasPrintout, sampleLine, type GasLine, type GasPrintout } from './gas';
import { transformScale } from './env';
import './clinical.css';

export type SlipRowState = 'idle' | 'locked' | 'dim' | 'correct' | 'wrong';

/** Tap the culprit: what each row stands for and how it looks. */
export interface SlipPick {
  /** The answer option for each row, by row index. Rows without one are printed but not tappable. */
  rowOption: ReadonlyArray<string | undefined>;
  stateOf: (optionId: string) => SlipRowState;
  disabled?: boolean;
  onPick?: (optionId: string) => void;
  /** At the reveal: the share of the room that tapped each row. */
  pctOf?: (optionId: string) => number;
  /** The option this player tapped. */
  mine?: string;
}

export interface BloodsSlipProps {
  graphic: BloodsGraphic;
  pick?: SlipPick;
  /** True at the reveal: no printing animation, pen marks on the culprit and a wrong pick. */
  revealed?: boolean;
  /** The question: a blood gas prints its sample type and oxygen from where the question shows it. */
  stem?: string;
}

const COUNT_MS = 620;
const FIRST_MS = 160;
const STEP_MS = 90;

const flagWord = (f?: 'H' | 'L') => (f === 'H' ? 'high' : f === 'L' ? 'low' : '');
export function rowLabel(r: BloodsRow): string {
  const unit = r.unit ? ` ${r.unit}` : '';
  const flag = r.flag ? `, ${flagWord(r.flag)}` : '';
  const range = r.range && r.range !== '—' ? `. Reference range ${r.range}` : '';
  return `${r.test} ${r.value}${unit}${flag}${range}`;
}

/** A blood gas line in words: the parameter's name, the value as printed and its range without the analyser's brackets. */
const gasLabel = (l: GasLine) => rowLabel({ ...l.row, test: l.spoken, value: l.value, range: l.range.replace(/^\[|\]$/g, '') });
/** The figure's name: a blood gas says its sample and oxygen. */
function slipName(graphic: BloodsGraphic, gas?: GasPrintout): string {
  if (!gas) return `${graphic.title} results`;
  return ['Blood gas results', gas.sample && `${gas.sample.toLowerCase()} sample`, gas.oxygen].filter(Boolean).join(', ');
}

export function BloodsSlip({ graphic, pick, revealed = false, stem }: BloodsSlipProps) {
  const reduce = useReducedMotion() ?? false;
  const animateIn = !revealed;
  const valueRefs = useRef<Array<HTMLElement | null>>([]);
  const gas = useMemo(() => gasPrintout(graphic, stem), [graphic, stem]);
  // The rows in print order, each with the index of its card row (which the count-up, the options and the room's share follow).
  const printed = useMemo<Array<{ index: number; row: BloodsRow; value: string; line?: GasLine }>>(
    () => (gas ? gas.lines.map((line) => ({ index: line.index, row: line.row, value: line.value, line })) : graphic.rows.map((row, index) => ({ index, row, value: row.value }))),
    [gas, graphic.rows],
  );
  const targets = useMemo(() => {
    const out = graphic.rows.map((r) => ({ text: r.value, delayMs: FIRST_MS }));
    printed.forEach((p, pos) => { out[p.index] = { text: p.value, delayMs: FIRST_MS + pos * STEP_MS }; });
    return out;
  }, [graphic.rows, printed]);
  const runKey = useMemo(() => targets.map((t) => t.text).join('|'), [targets]);
  useCountUps(valueRefs, targets, COUNT_MS, runKey, animateIn);
  const hasRanges = graphic.rows.some((r) => r.range && r.range !== '—');
  // Open: the rows are the answer area and not yet revealed. Live: this player can still tap one.
  const open = Boolean(pick) && !revealed;
  const live = open && !pick?.disabled;
  const headLine = gas ? sampleLine(gas) : '';
  // The widest name and range on the card, so every line of the printout keeps the same columns.
  const columns = gas
    ? { '--gas-name-ch': Math.max(...gas.lines.map((l) => [...l.label].length)), '--gas-range-ch': Math.max(1, ...gas.lines.map((l) => [...l.range].length)) } as CSSProperties
    : undefined;

  return (
    <motion.figure
      className="rx-slip"
      data-kind={gas ? 'gas' : undefined}
      data-revealed={revealed ? '' : undefined}
      style={columns}
      initial={animateIn && !reduce ? { opacity: 0, y: 14, rotate: -0.8 } : false}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
      aria-label={slipName(graphic, gas)}
    >
      <figcaption className="rx-slip-head">
        <span className="rx-slip-title">{gas ? 'Blood gas' : graphic.title}</span>
        {live ? <span className="rx-slip-cue">Tap a row</span> : hasRanges && <span>UK adult ranges</span>}
        {headLine && <span className="rx-gas-sample">{chem(headLine)}</span>}
      </figcaption>
      <ul className="rx-slip-rows" data-open={open ? '' : undefined}>
        {printed.map((p, pos) => {
          const { index: i, row: r, line } = p;
          const optionId = pick?.rowOption[i];
          const popMs = animateIn && !reduce ? FIRST_MS + pos * STEP_MS + COUNT_MS : 0;
          const pct = revealed && optionId && pick?.pctOf ? pick.pctOf(optionId) : undefined;
          const body = line
            ? <GasRowBody line={line} position={pos} popMs={popMs} valueRefs={valueRefs} pct={pct} />
            : <RowBody row={r} index={i} position={pos} popMs={popMs} valueRefs={valueRefs} pct={pct} />;
          const said = line ? gasLabel(line) : rowLabel(r);
          // A blood gas prints its electrolytes and glucose under a rule, as the analyser does.
          const blockStart = line && line.block === 1 && printed[pos - 1]?.line?.block === 0 ? '' : undefined;
          if (!pick || !optionId) {
            return (
              <li key={`${r.test}-${i}`} data-block-start={blockStart}>
                <div className="rx-row" data-flag={r.flag} style={{ '--rx-pop': `${popMs}ms` } as CSSProperties}>
                  <span className="sr-only">{said}</span>
                  <span aria-hidden="true" style={{ display: 'contents' }}>{body}</span>
                </div>
              </li>
            );
          }
          const state = pick.stateOf(optionId);
          const mine = pick.mine === optionId;
          return (
            <li key={`${r.test}-${i}`} data-block-start={blockStart}>
              <SpotRow row={r} said={said} optionId={optionId} state={state} mine={mine} revealed={revealed} popMs={popMs}
                open={open} live={live} disabled={pick.disabled} onPick={pick.onPick}>
                {body}
              </SpotRow>
            </li>
          );
        })}
      </ul>
    </motion.figure>
  );
}

function RowBody({ row, index, position, popMs, valueRefs, pct }: { row: BloodsRow; index: number; position: number; popMs: number; valueRefs: RefObject<Array<HTMLElement | null>>; pct?: number }) {
  const reduce = useReducedMotion() ?? false;
  return (
    <>
      <span className="rx-row-test">
        <span className="rx-row-name">{chem(row.test)}</span>
        {row.range && row.range !== '—' && <span className="rx-row-range">{row.range}</span>}
      </span>
      <span className="rx-row-result">
        <span className="rx-row-value" ref={(el) => { valueRefs.current[index] = el; }}>{row.value}</span>
        {row.unit && <span className="rx-row-unit">{row.unit}</span>}
      </span>
      <span className="rx-row-side">
        {row.flag && (
          <motion.span
            className="rx-flag"
            initial={popMs > 0 && !reduce ? { scale: 0.2, rotate: -16, opacity: 0 } : false}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ delay: popMs / 1000, type: 'spring', stiffness: 560, damping: 15 }}
          >
            {row.flag === 'H' ? '↑H' : '↓L'}
          </motion.span>
        )}
        {pct !== undefined && <span className="rx-row-pct">{pct}%</span>}
      </span>
      {pct !== undefined && <RoomShare pct={pct} position={position} reduce={reduce} />}
    </>
  );
}

/** The room's share of a row at the reveal: a thin bar along its foot, filled in from the left. */
function RoomShare({ pct, position, reduce }: { pct: number; position: number; reduce: boolean }) {
  return (
    <span className="rx-row-split" aria-hidden="true">
      <motion.i initial={reduce ? false : { scaleX: 0 }} animate={{ scaleX: Math.max(0.02, pct / 100) }} transition={{ type: 'spring', stiffness: 120, damping: 20, delay: 0.1 + position * 0.05 }} />
    </span>
  );
}

/**
 * One line of a blood gas analyser's report: the analyser's name for the parameter, the value and its unit, an arrow beside a
 * value out of range (↑ high, ↓ low, never colour alone), the bracketed reference range, and the room's share at the reveal.
 */
function GasRowBody({ line, position, popMs, valueRefs, pct }: { line: GasLine; position: number; popMs: number; valueRefs: RefObject<Array<HTMLElement | null>>; pct?: number }) {
  const reduce = useReducedMotion() ?? false;
  const { row, index } = line;
  return (
    <>
      <span className="rx-row-test"><span className="rx-row-name">{chem(line.label)}</span></span>
      <span className="rx-row-result">
        <span className="rx-row-value" ref={(el) => { valueRefs.current[index] = el; }}>{line.value}</span>
        {row.unit && <span className="rx-row-unit">{row.unit}</span>}
      </span>
      <span className="rx-gas-flag">
        {row.flag && (
          <motion.span
            initial={popMs > 0 && !reduce ? { scale: 0.2, opacity: 0 } : false}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: popMs / 1000, type: 'spring', stiffness: 560, damping: 15 }}
          >
            {row.flag === 'H' ? '↑' : '↓'}
          </motion.span>
        )}
      </span>
      <span className="rx-gas-range">{line.range}</span>
      <span className="rx-row-side">{pct !== undefined && <span className="rx-row-pct">{pct}%</span>}</span>
      {pct !== undefined && <RoomShare pct={pct} position={position} reduce={reduce} />}
    </>
  );
}

interface PenGeometry { rowW: number; cell: { x: number; y: number; w: number; h: number } }

const LOOP_PAD_X = 16;
const LOOP_PAD_Y = 13;

function SpotRow({ row, said, optionId, state, mine, revealed, popMs, open, live, disabled, onPick, children }: {
  row: BloodsRow; said: string; optionId: string; state: SlipRowState; mine: boolean; revealed: boolean; popMs: number;
  /** The slip is the answer area and not yet revealed: the row keeps a button's shape. */
  open: boolean;
  /** This player can still tap: the lip, the chevron and the pressed state. */
  live: boolean;
  disabled?: boolean; onPick?: (optionId: string) => void; children: ReactNode;
}) {
  const reduce = useReducedMotion() ?? false;
  const ref = useRef<HTMLButtonElement>(null);
  const [geo, setGeo] = useState<PenGeometry>();
  const marked = revealed && (state === 'correct' || (state === 'wrong' && mine));
  // The option buttons' lock-in squash, played once as the row goes from open to locked.
  const prev = useRef<SlipRowState>(state);
  useLayoutEffect(() => {
    const el = ref.current; const from = prev.current; prev.current = state;
    if (!el || from === state || state !== 'locked' || !canAnimate() || prefersReducedMotion()) return;
    el.animate([
      { transform: 'translate3d(0,0,0) scale(1)' }, { transform: 'translate3d(0,2px,0) scale(0.96)', offset: 0.22 },
      { transform: 'translate3d(0,1px,0) scale(1.02)', offset: 0.6 }, { transform: 'translate3d(0,0,0) scale(1)' },
    ], { duration: 380, easing: 'ease-out' });
  }, [state]);
  // The pen goes round the result itself, as a doctor circles a number on a lab slip; the note sits beside it. Measured in layout
  // pixels (the screen's rectangles divided by the scale the row is shown at), so the marks land on the result inside a scaled
  // ancestor too: the presenter view's 1920 × 1080 frame and FitGraphic's scale.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!marked || !el) return;
    const r = el.getBoundingClientRect();
    const c = el.querySelector('.rx-row-result')?.getBoundingClientRect();
    if (!c || r.width <= 0 || c.width <= 0) return;
    const k = transformScale(el);
    setGeo({ rowW: r.width / k, cell: { x: (c.left - r.left) / k, y: (c.top - r.top) / k, w: c.width / k, h: c.height / k } });
  }, [marked]);
  const tone = state === 'correct' ? 'correct' : 'wrong';
  const spoken = `${said}.${state === 'correct' && revealed ? ' The culprit.' : ''}${mine ? (revealed ? ' Your pick.' : ' Locked in.') : ''}`;
  // The note sits just above the row, ending where the pen mark starts, in the gap between the rows.
  const scribbleStyle: CSSProperties | undefined = geo ? { right: geo.rowW - geo.cell.x + LOOP_PAD_X * (state === 'correct' ? 1 : 0.25), top: -13 } : undefined;
  return (
    <button
      ref={ref}
      type="button"
      className="rx-row"
      data-tappable=""
      data-open={open ? '' : undefined}
      data-live={live ? '' : undefined}
      data-option={optionId}
      data-state={state}
      data-mine={mine ? '' : undefined}
      data-flag={row.flag}
      style={{ '--rx-pop': `${popMs}ms` } as CSSProperties}
      aria-pressed={state === 'locked' || (mine && revealed)}
      aria-label={spoken}
      disabled={disabled}
      onClick={() => onPick?.(optionId)}
    >
      {children}
      {open && <span className="rx-row-go" aria-hidden="true">{live && <TapMark />}</span>}
      {marked && geo && <PenMark tone={tone} cell={geo.cell} reduce={reduce} />}
      {marked && (
        <motion.span className="rx-scribble" data-tone={tone} aria-hidden="true" style={scribbleStyle ?? { right: '4.2rem', top: -12 }}
          initial={reduce ? { opacity: 0, rotate: -5 } : { opacity: 0, scale: 0.6, rotate: -14 }} animate={{ opacity: 1, scale: 1, rotate: -5 }}
          transition={reduce ? { delay: 0.3 } : { delay: 0.45, type: 'spring', stiffness: 380, damping: 18 }}>
          {state === 'correct' ? 'the culprit' : 'your pick'}
        </motion.span>
      )}
    </button>
  );
}

/** The mark at the right of a row that can be tapped: a chevron in the pen's own line, in the ink. */
function TapMark() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M9.5 6l6 6-6 6" />
    </svg>
  );
}

/** A loop drawn round the result (the culprit), or a stroke through it (a wrong pick), drawn in by hand. */
function PenMark({ tone, cell, reduce }: { tone: 'correct' | 'wrong'; cell: { x: number; y: number; w: number; h: number }; reduce: boolean }) {
  const box = tone === 'correct'
    ? { left: cell.x - LOOP_PAD_X, top: cell.y - LOOP_PAD_Y, w: cell.w + 2 * LOOP_PAD_X, h: cell.h + 2 * LOOP_PAD_Y }
    : { left: cell.x - 6, top: cell.y, w: cell.w + 12, h: cell.h };
  const d = tone === 'correct' ? handLoop(box.w, box.h) : strike(box.w, box.h);
  return (
    <svg className="rx-pen" data-tone={tone} style={{ left: box.left, top: box.top }} width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`} aria-hidden="true">
      <motion.path d={d} initial={reduce ? { pathLength: 1, opacity: 0 } : { pathLength: 0, opacity: 0.2 }} animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: reduce ? 0.2 : tone === 'correct' ? 0.55 : 0.32, ease: [0.3, 0.7, 0.2, 1], delay: 0.08 }} />
    </svg>
  );
}

/** A loose oval inside the box that overshoots where it started, like a pen circling a result. */
export function handLoop(w: number, h: number): string {
  const cx = w / 2, cy = h / 2, rx = w / 2 - 2, ry = h / 2 - 3;
  const pts: Array<[number, number]> = [];
  const N = 30;
  for (let i = 0; i <= N; i++) {
    const a = ((196 + (384 * i) / N) * Math.PI) / 180;
    const wob = 1 + 0.03 * Math.sin(i * 1.9) + (i > N - 5 ? 0.025 * (i - N + 5) : 0);
    pts.push([cx + rx * wob * Math.cos(a), cy + ry * wob * Math.sin(a) - (i > N - 5 ? 0.9 * (i - N + 5) : 0)]);
  }
  return smooth(pts);
}

/** A slightly wavering stroke through the middle of the row. */
export function strike(w: number, h: number): string {
  const y = h * 0.55;
  return `M 2 ${(y + 2).toFixed(1)} C ${(w * 0.3).toFixed(1)} ${(y - 3).toFixed(1)}, ${(w * 0.62).toFixed(1)} ${(y + 4).toFixed(1)}, ${(w - 2).toFixed(1)} ${(y - 2).toFixed(1)}`;
}

/** Catmull–Rom through the points as cubic Béziers. */
function smooth(p: Array<[number, number]>): string {
  let d = `M ${p[0][0].toFixed(1)} ${p[0][1].toFixed(1)}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[Math.max(0, i - 1)], p1 = p[i], p2 = p[i + 1], p3 = p[Math.min(p.length - 1, i + 2)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
}

